import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../hooks/useRole';
import { useReadOnly } from '../hooks/useReadOnly';
import { customerService } from '../api';
import { Button, Input, ThemedText, Pagination, ActionMenu, ConfirmDialog, SkeletonTable, EmptyState, ErrorState, GenerateDemoDataDialog } from '../components';
import { CustomerFormModal } from '../components/CustomerFormModal';
import { usePagination } from '../hooks/usePagination';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '../components/TableStyles';
import { SearchContainer, StyledSearchContainer, SearchIcon, TransparentInput } from '../components/SearchStyles';
import { User, Search, ChevronRight, Sparkles } from 'lucide-react';

export const CustomersPage = () => {
  const theme = useTheme();
  const { isAdmin } = useRole();
  const { user } = useAuth();
  const readOnly = useReadOnly();
  const [isDemoDataOpen, setIsDemoDataOpen] = useState(false);
  // "Generate Demo Data" is for demo-org builders only; read-only viewers would 403 on commit.
  const canGenerateDemoData = user?.isDemoOrg === true && !readOnly;
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const {
    currentPage,
    totalCount,
    totalPages,
    pageSize,
    startIndex,
    endIndex,
    goToPage,
    resetPage,
    setTotalCount,
    paginationParams,
    extractPagedData,
  } = usePagination(20);

  useEffect(() => {
    fetchCustomers();
  }, [currentPage]);

  // Reset to page 1 on search change
  useEffect(() => {
    resetPage();
  }, [searchTerm]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const response = await customerService.getCustomers(paginationParams);

      const { items: customersData, totalCount: total } = extractPagedData(response);
      setCustomers(customersData);
      setTotalCount(total);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setFetchError(error?.response?.data?.message || 'Failed to load customers. Please check your connection and try again.');
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomer = async (id) => {
    try {
      await customerService.deleteCustomer(id);
      toast.success('Customer deleted successfully');
      setDeleteConfirmId(null);
      fetchCustomers();
    } catch (error) {
      console.error('Failed to delete customer:', error);
      toast.error(error.response?.data?.message || 'Failed to delete customer');
    }
  };

  const openEditModal = (customer) => {
    setSelectedCustomer(customer);
    setIsEditModalOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredCustomers = customers.filter(customer =>
    customer.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${customer.firstName} ${customer.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout currentPath="/customers">
      <PageContainer theme={theme} style={{ maxWidth: '100%' }}>
        <PageHeader theme={theme} style={{ alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#43474f', fontWeight: 500, marginBottom: 10 }}>
              <span>Operations</span>
              <ChevronRight size={12} />
              <span style={{ color: '#232562', fontWeight: 700 }}>Customers</span>
            </div>
            <h1 style={{ fontFamily: "'Manrope', sans-serif", fontSize: '1.875rem', fontWeight: 800, color: '#232562', margin: '0 0 6px 0', letterSpacing: '-0.02em', lineHeight: 1.2 }}>Customers</h1>
            <p style={{ color: '#43474f', fontSize: '0.875rem', margin: 0 }}>Manage your customers</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {canGenerateDemoData && (
              <Button
                variant="secondary"
                onClick={() => setIsDemoDataOpen(true)}
                title="Generate realistic demo customers"
              >
                <Sparkles size={14} /> Generate Demo Data
              </Button>
            )}
            <Button onClick={() => setIsCreateModalOpen(true)}>
              + New Customer
            </Button>
          </div>
        </PageHeader>

        <SearchContainer theme={theme}>
          <StyledSearchContainer theme={theme}>
            <SearchIcon><Search size={16} /></SearchIcon>
            <TransparentInput
              placeholder="Search by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              theme={theme}
            />
          </StyledSearchContainer>
        </SearchContainer>

        <TableContainer theme={theme}>
          {loading ? (
            <SkeletonTable rows={5} cols={5} theme={theme} />
          ) : fetchError ? (
            <ErrorState
              theme={theme}
              title="Failed to load customers"
              message={fetchError}
              onRetry={fetchCustomers}
            />
          ) : filteredCustomers.length === 0 ? (
            <EmptyState
              theme={theme}
              icon={<User size={48} />}
              title="No customers found"
              description={searchTerm ? 'Try adjusting your search term' : 'Add your first customer to get started'}
            />
          ) : (
            <Table>
              <Thead theme={theme}>
                <tr>
                  <Th theme={theme}>Name</Th>
                  <Th theme={theme}>Email</Th>
                  <Th theme={theme}>Phone</Th>
                  <Th theme={theme}>Company</Th>
                  <Th theme={theme}>Created</Th>
                  <Th theme={theme} style={{ width: '50px' }}></Th>
                </tr>
              </Thead>
              <Tbody>
                {filteredCustomers.map(customer => (
                  <Tr key={customer.id} theme={theme}>
                    <Td theme={theme}>
                      <ThemedText type="defaultSemiBold">
                        {customer.firstName} {customer.lastName}
                      </ThemedText>
                    </Td>
                    <Td theme={theme}>
                      <ThemedText type="small">{customer.email}</ThemedText>
                    </Td>
                    <Td theme={theme}>
                      <ThemedText type="small">{customer.phone || 'N/A'}</ThemedText>
                    </Td>
                    <Td theme={theme}>
                      <ThemedText type="small">{customer.company || 'N/A'}</ThemedText>
                    </Td>
                    <Td theme={theme}>
                      <ThemedText type="small">{formatDate(customer.createdAt)}</ThemedText>
                    </Td>
                    <Td theme={theme}>
                      <ActionMenu actions={[
                        { label: 'Edit', onClick: () => openEditModal(customer) },
                        { label: 'Delete', onClick: () => setDeleteConfirmId(customer.id), variant: 'danger' },
                      ]} />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </TableContainer>

        {/* Pagination */}
        {!loading && customers.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            startIndex={startIndex}
            endIndex={endIndex}
            onPageChange={goToPage}
            itemLabel="customers"
          />
        )}

        <CustomerFormModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            fetchCustomers();
          }}
        />

        <CustomerFormModal
          isOpen={isEditModalOpen}
          onClose={() => { setIsEditModalOpen(false); setSelectedCustomer(null); }}
          onSuccess={() => {
            setIsEditModalOpen(false);
            setSelectedCustomer(null);
            fetchCustomers();
          }}
          editCustomer={selectedCustomer}
        />

        <ConfirmDialog
          isOpen={deleteConfirmId !== null}
          title="Delete Customer"
          message="Are you sure you want to delete this customer? This action cannot be undone."
          onConfirm={() => handleDeleteCustomer(deleteConfirmId)}
          onCancel={() => setDeleteConfirmId(null)}
        />

        {canGenerateDemoData && (
          <GenerateDemoDataDialog
            entityType="customers"
            targetId={user?.organizationId}
            entityName="Customers"
            entityNoun="customers"
            open={isDemoDataOpen}
            onClose={() => setIsDemoDataOpen(false)}
            onCommitted={() => { setIsDemoDataOpen(false); window.location.reload(); }}
          />
        )}
      </PageContainer>
    </Layout>
  );
};

export default CustomersPage;
